// External dependencies
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';

// Internal dependencies
import AdminLayout from '@/modules/admin/client/components/AdminLayout.component';
import CmsAdminNav from '@/modules/cms/client/components/CmsAdminNav.component';
import {
  createBlogCategory,
  deleteBlogCategory,
  getBlogCategories,
  updateBlogCategory,
} from '@/modules/cms/client/api/admin-cms.api';
import { isBlogCategoriesEnabled } from '@/modules/cms/client/utils/feature-flags';
import { getLocales } from '@/modules/core/client/utils/locales';

/**
 * Pull the server's message out of a failed request, if it sent one.
 *
 * @param {Error} err - Axios error
 * @param {String} fallback - Message when the server sent none
 * @returns {String}
 */
function errorMessage(err, fallback) {
  const message =
    err && err.response && err.response.data && err.response.data.message;
  return message || fallback;
}

/**
 * Persist a new order after moving the item at `index` by `direction`.
 *
 * Writes only the two items that swapped, as the footer menu manager does.
 *
 * @param {Array} list - Items in display order
 * @param {Number} index - Index being moved
 * @param {Number} direction - -1 (up) or 1 (down)
 * @returns {Promise<Array|null>} Reordered list, or null when at an edge
 */
async function persistMove(list, index, direction) {
  const target = index + direction;
  if (target < 0 || target >= list.length) {
    return null;
  }

  const reordered = [...list];
  [reordered[index], reordered[target]] = [reordered[target], reordered[index]];

  const first = Math.min(index, target);
  await Promise.all(
    [reordered[first], reordered[first + 1]].map((item, offset) =>
      updateBlogCategory(item._id, { order: first + offset }),
    ),
  );

  return reordered.map((item, position) => ({ ...item, order: position }));
}

/**
 * Admin screen for blog categories: the ordered list for one language on the
 * left, the selected category's details on the right.
 */
export default function AdminCmsBlogCategories() {
  const [locale, setLocale] = useState('en');
  const [categories, setCategories] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(null);
  const [newName, setNewName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const locales = getLocales();
  const selected = categories.find(category => category._id === selectedId);

  const notify = (message, isError = false) => {
    setError(isError ? message : null);
    setSuccess(isError ? null : message);
  };

  const isEnabled = isBlogCategoriesEnabled();

  const load = async () => {
    // The API answers 404 while the feature is off; don't turn that into a
    // misleading "failed to load".
    if (!isEnabled) {
      return;
    }

    setIsLoading(true);
    try {
      const data = await getBlogCategories({ locale });
      setCategories(data);
      if (!data.some(category => category._id === selectedId)) {
        setSelectedId(data.length ? data[0]._id : null);
      }
    } catch (err) {
      notify('Failed to load categories.', true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setSelectedId(null);
    load();
  }, [locale]);

  useEffect(() => {
    setForm(
      selected
        ? {
            name: selected.name,
            slug: selected.slug,
            description: selected.description || '',
            enabled: selected.enabled !== false,
          }
        : null,
    );
  }, [selectedId, categories]);

  const handleCreate = async event => {
    event.preventDefault();
    const name = newName.trim();
    if (!name) {
      return;
    }

    setIsSaving(true);
    try {
      const created = await createBlogCategory({ name, locale });
      setNewName('');
      setCategories(previous => [...previous, created]);
      setSelectedId(created._id);
      notify(`Category “${created.name}” created.`);
    } catch (err) {
      notify(errorMessage(err, 'Failed to create category.'), true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async event => {
    event.preventDefault();
    if (!selected || !form) {
      return;
    }

    setIsSaving(true);
    try {
      const saved = await updateBlogCategory(selected._id, form);
      setCategories(previous =>
        previous.map(category =>
          category._id === saved._id ? saved : category,
        ),
      );
      notify('Category saved.');
    } catch (err) {
      notify(errorMessage(err, 'Failed to save category.'), true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selected) {
      return;
    }
    if (!window.confirm(`Delete the category “${selected.name}”?`)) {
      return;
    }

    try {
      await deleteBlogCategory(selected._id);
      notify(`Category “${selected.name}” deleted.`);
      setSelectedId(null);
      load();
    } catch (err) {
      // 409 carries the reason (posts still use it).
      notify(errorMessage(err, 'Failed to delete category.'), true);
    }
  };

  const handleMove = async (index, direction) => {
    try {
      const reordered = await persistMove(categories, index, direction);
      if (reordered) {
        setCategories(reordered);
      }
    } catch (err) {
      notify('Failed to reorder categories.', true);
    }
  };

  return (
    <AdminLayout>
      <CmsAdminNav />
      <div className="container">
        <div className="row" style={{ marginBottom: '1rem' }}>
          <div className="col-sm-9">
            <h2>Blog categories</h2>
            <p>
              Each post belongs to at most one category. Readers browse them
              from the blog sidebar and at{' '}
              <code>/blog/category/&lt;slug&gt;</code>. A hidden category
              disappears from the blog; its posts stay in the main list.
            </p>
          </div>
          <div className="col-sm-3" style={{ marginTop: '20px' }}>
            <label htmlFor="admin-blog-categories-locale">Language</label>
            <select
              id="admin-blog-categories-locale"
              className="form-control"
              value={locale}
              onChange={event => setLocale(event.target.value)}
            >
              {locales.map(item => (
                <option key={item.code} value={item.code}>
                  {item.label} ({item.code})
                </option>
              ))}
            </select>
          </div>
        </div>

        {!isEnabled && (
          <div className="alert alert-warning">
            Blog categories are switched off. Enable them with{' '}
            <code>featureFlags.blogCategories: true</code> in the server
            configuration; until then the blog shows no categories and this
            screen is read-only.
          </div>
        )}

        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <div className="row">
          <div className="col-sm-6">
            <div className="panel panel-default">
              <div className="panel-heading">Categories</div>
              {isLoading ? (
                <div className="panel-body">Loading…</div>
              ) : (
                <div className="list-group">
                  {categories.map((category, index) => (
                    <div
                      key={category._id}
                      className={classnames('list-group-item', {
                        active: category._id === selectedId,
                      })}
                    >
                      <div className="row">
                        <div className="col-xs-8">
                          <a
                            href={`#category-${category._id}`}
                            className="admin-faq-category-link"
                            onClick={event => {
                              event.preventDefault();
                              setSelectedId(category._id);
                            }}
                          >
                            {category.name}
                          </a>{' '}
                          <span className="badge">{category.postCount}</span>
                          {category.enabled === false && (
                            <span
                              className="label label-default"
                              style={{ marginLeft: '5px' }}
                            >
                              Hidden
                            </span>
                          )}
                        </div>
                        <div className="col-xs-4 text-right">
                          <div className="btn-group btn-group-xs" role="group">
                            <button
                              type="button"
                              className="btn btn-default"
                              aria-label="Move category up"
                              disabled={index === 0}
                              onClick={() => handleMove(index, -1)}
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              className="btn btn-default"
                              aria-label="Move category down"
                              disabled={index === categories.length - 1}
                              onClick={() => handleMove(index, 1)}
                            >
                              ↓
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {!categories.length && (
                    <div className="list-group-item">
                      No categories in this language yet.
                    </div>
                  )}
                </div>
              )}
              <div className="panel-footer">
                <form onSubmit={handleCreate}>
                  <label htmlFor="admin-blog-category-new" className="sr-only">
                    New category name
                  </label>
                  <div className="input-group">
                    <input
                      id="admin-blog-category-new"
                      type="text"
                      className="form-control"
                      value={newName}
                      onChange={event => setNewName(event.target.value)}
                      placeholder="New category name"
                    />
                    <span className="input-group-btn">
                      <button
                        type="submit"
                        className="btn btn-default"
                        disabled={isSaving || !newName.trim()}
                      >
                        Add
                      </button>
                    </span>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="col-sm-6">
            {selected && form ? (
              <form className="panel panel-default" onSubmit={handleSave}>
                <div className="panel-heading">Edit category</div>
                <div className="panel-body">
                  <div className="form-group">
                    <label htmlFor="admin-blog-category-name">Name</label>
                    <input
                      id="admin-blog-category-name"
                      type="text"
                      className="form-control"
                      value={form.name}
                      onChange={event =>
                        setForm({ ...form, name: event.target.value })
                      }
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="admin-blog-category-slug">Slug</label>
                    <input
                      id="admin-blog-category-slug"
                      type="text"
                      className="form-control"
                      value={form.slug}
                      onChange={event =>
                        setForm({ ...form, slug: event.target.value })
                      }
                      required
                    />
                    <span className="help-block">
                      Public URL: <code>/blog/category/{form.slug}</code>.
                      Changing it breaks existing links.
                    </span>
                  </div>
                  <div className="form-group">
                    <label htmlFor="admin-blog-category-description">
                      Description
                    </label>
                    <textarea
                      id="admin-blog-category-description"
                      className="form-control"
                      rows="2"
                      value={form.description}
                      onChange={event =>
                        setForm({ ...form, description: event.target.value })
                      }
                      placeholder="Optional one-liner shown above the category's posts"
                    />
                  </div>
                  <div className="checkbox">
                    <label htmlFor="admin-blog-category-enabled">
                      <input
                        id="admin-blog-category-enabled"
                        type="checkbox"
                        checked={form.enabled}
                        onChange={event =>
                          setForm({ ...form, enabled: event.target.checked })
                        }
                      />
                      Shown on the blog
                    </label>
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSaving}
                    style={{ marginRight: '5px' }}
                  >
                    Save category
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDelete}
                  >
                    Delete
                  </button>
                  {selected.postCount > 0 && (
                    <span className="help-block">
                      Used by {selected.postCount} post(s). Reassign them before
                      deleting.
                    </span>
                  )}
                </div>
              </form>
            ) : (
              <p className="text-muted">
                Select a category to edit it, or add one.
              </p>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

AdminCmsBlogCategories.propTypes = {};
